import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, query, orderBy, getDocs, where } from 'firebase/firestore';
import { Link } from 'react-router-dom';
// 'MessageSquare' has been removed from this import line
import { Loader2, GitBranch, X, ShieldCheck } from 'lucide-react'; 

// --- Modal Component ---
const DetailModal = ({ node, onClose }) => {
    const [relatedPosts, setRelatedPosts] = useState([]);
    const [loadingPosts, setLoadingPosts] = useState(false);

    useEffect(() => {
        if (!node) {
            setRelatedPosts([]);
            return;
        }

        const fetchRelatedPosts = async () => {
            setLoadingPosts(true);
            const postsQuery = query(
                collection(db, 'posts'),
                where('fraudMatrixId', '==', node.id)
            );
            const postsSnapshot = await getDocs(postsQuery);
            const postsData = postsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setRelatedPosts(postsData);
            setLoadingPosts(false);
        };

        fetchRelatedPosts();
    }, [node]);

    if (!node) return null;

    const hasCryptos = node.cryptos && node.cryptos.length > 0;

    return (
        <div className="fixed inset-0 bg-black/60 z-50 flex justify-center items-center p-4">
            <div className="glass-card rounded-2xl p-8 max-w-3xl w-full relative border-cyan-500/30 border max-h-[90vh] flex flex-col">
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors z-10">
                    <X size={24} />
                </button>
                <div className="flex-shrink-0">
                    <div className="flex items-center mb-4">
                        <GitBranch size={24} className="text-cyan-400 mr-3" />
                        <div>
                            <h2 className="text-3xl font-bold text-white">{node.name}</h2>
                            <p className="text-gray-500 font-mono">{node.id}</p>
                        </div>
                    </div>
                    <div className="mb-6">
                        <h4 className="font-semibold text-lg text-white mb-2">Description</h4>
                        <p className="text-gray-300 leading-relaxed">{node.description}</p>
                    </div>
                    {hasCryptos && (
                        <div className="mb-6">
                            <h4 className="font-semibold text-lg text-white mb-3">Commonly Targeted Cryptocurrencies</h4>
                            <div className="flex flex-wrap gap-2">
                                {node.cryptos.map(crypto => (
                                    <div key={crypto.value} className="flex items-center bg-slate-800/50 px-3 py-1 rounded-full text-sm">
                                        <ShieldCheck size={14} className="text-green-400 mr-2" />
                                        <span className="text-slate-300">{crypto.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="flex-grow overflow-y-auto pr-2">
                    <h4 className="font-semibold text-lg text-white mb-3">Related Community Reports</h4>
                    {loadingPosts ? (
                        <div className="flex justify-center items-center h-24">
                            <Loader2 className="animate-spin text-cyan-400" size={24} />
                        </div>
                    ) : relatedPosts.length > 0 ? (
                        <div className="space-y-3">
                            {relatedPosts.map(post => (
                                <Link to={`/post/${post.id}`} key={post.id} className="block glass-card p-3 rounded-lg hover:bg-white/5 transition-colors">
                                    <p className="font-semibold text-white truncate">{post.title}</p>
                                    <div className="flex items-center justify-between text-xs text-gray-400 mt-1">
                                        <span>by {post.authorEmail?.split('@')[0] || 'Anonymous'}</span>
                                        <span>{post.createdAt ? new Date(post.createdAt.toDate()).toLocaleDateString() : ''}</span>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    ) : (
                        <p className="text-gray-500 text-center py-4">No community reports have been tagged with this fraud type yet.</p>
                    )}
                </div>
            </div>
        </div>
    );
};


const TreeNode = ({ node, allNodes, onNodeClick }) => {
  const children = allNodes.filter(childNode => childNode.parentId === node.id);

  return (
    <li className="ml-8 mt-1"> 
      <div 
        onClick={() => onNodeClick(node)} 
        className="flex items-center cursor-pointer group p-1 rounded-md hover:bg-white/5 transition-colors"
      >
        <GitBranch size={16} className="text-cyan-400 mr-2" />
        <strong className="text-white text-lg group-hover:text-cyan-300 transition-colors">{node.name}</strong>
        <span className="text-gray-500 text-sm ml-2">({node.id})</span>
      </div>
      {children.length > 0 && (
        <ul className="pl-6 border-l-2 border-gray-800 ml-2">
          {children.map(child => <TreeNode key={child.id} node={child} allNodes={allNodes} onNodeClick={onNodeClick} />)}
        </ul>
      )}
    </li>
  );
};

export default function FraudMatrixPage() {
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNode, setSelectedNode] = useState(null);

  useEffect(() => {
    const fetchNodes = async () => {
      const q = query(collection(db, 'fraudMatrix'), orderBy('name'));
      const snapshot = await getDocs(q);
      setNodes(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    };
    fetchNodes();
  }, []);

  const handleNodeClick = (node) => {
    setSelectedNode(node);
  };

  const handleCloseModal = () => {
    setSelectedNode(null);
  };

  const rootNodes = nodes.filter(node => !node.parentId);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <Loader2 className="animate-spin text-cyan-400" size={48} />
        <p className="text-gray-400 mt-4">Loading Fraud Matrix...</p>
      </div>
    );
  }

  return (
    <>
      <DetailModal node={selectedNode} onClose={handleCloseModal} />
      <div className="glass-card rounded-2xl p-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Crypto Fraud Exposé Matrix</h1>
          <p className="text-gray-400">A framework of common tactics and techniques used in crypto fraud. Click any item for details.</p>
        </div>
        
        {rootNodes.length > 0 ? (
          <ul>
            {rootNodes.map(node => <TreeNode key={node.id} node={node} allNodes={nodes} onNodeClick={handleNodeClick} />)}
          </ul>
        ) : (
          <div className="text-center py-16">
            <h3 className="text-xl font-semibold text-white">The Fraud Matrix is empty</h3>
            <p className="text-gray-500 mt-2">Administrator needs to add data to the 'fraudMatrix' collection in Firestore.</p>
          </div>
        )}
      </div>
    </>
  );
}